import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DrawerBookingProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  position?: 'right' | 'left';
  width?: string;
  className?: string;
}

const DrawerBooking: React.FC<DrawerBookingProps> = ({
  isOpen,
  onClose,
  children,
  title,
  position = 'right',
  width = '400px',
  className = ''
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  
  // Gérer l'animation d'ouverture/fermeture
  useEffect(() => {
    if (isOpen) {
      // Petit délai pour permettre au DOM de se mettre à jour avant l'animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Gérer la fermeture avec animation
  const handleClose = () => {
    setIsVisible(false);
    // Attendre que l'animation se termine avant d'exécuter onClose
    setTimeout(onClose, 300);
  };
  
  // Ne rien rendre si le drawer n'est pas ouvert
  if (!isOpen) return null;
  
  // Calculer les classes CSS pour l'animation
  const translateClass = position === 'right' 
    ? (isVisible ? 'translate-x-0' : 'translate-x-full')
    : (isVisible ? 'translate-x-0' : '-translate-x-full');
    
  const overlayClass = isVisible ? 'opacity-100' : 'opacity-0';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay avec animation */}
      <div 
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${overlayClass}`}
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 flex max-w-full md:pl-10 z-50">
        <div
          className={`
            relative w-screen md:max-w-md transform transition-transform duration-300 ease-in-out ${translateClass}
            bg-white md:rounded-l-3xl shadow-xl flex flex-col
            ${className}
          `}
          style={{ width: window.innerWidth >= 768 ? width : '100%' }}
        >
          {/* En-tête avec titre et bouton de fermeture */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-xl font-medium text-gray-900">
              {title || t('common.details', 'Détails')}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={t('common.close', 'Fermer') as string}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          
          {/* Contenu du drawer avec défilement */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawerBooking;
